import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';

export interface RecentActionEntry {
    actionLabel: string;
    completedAt: number;
}

export type RecentActionsMap = Record<string, RecentActionEntry>;

interface RecentActionsContextType {
    /** Patient id -> the action that patient's row last had before the
     *  current user resolved it, while the shared sliding window is open. */
    recentActions: RecentActionsMap;
    markActionCompleted: (patientId: string, actionLabel: string) => void;
}

const RecentActionsContext = createContext<RecentActionsContextType | undefined>(undefined);

const STORAGE_KEY = 'odo_recent_actions';
// Global sliding window: every markActionCompleted call resets the clock for
// ALL held entries. The whole map stays alive until this long after the most
// recent action, then clears in one shot.
const RECENT_ACTION_WINDOW_MS = 30 * 60 * 1000;
// Not a real growth limit - just a guard against an unbounded sessionStorage
// blob if a lot of actions get resolved within one window.
const MAX_RECENT_ACTIONS = 50;

function isRecentActionEntry(value: unknown): value is RecentActionEntry {
    if (!value || typeof value !== 'object') return false;
    const entry = value as Record<string, unknown>;
    return typeof entry.actionLabel === 'string' && typeof entry.completedAt === 'number';
}

/** Most recent `completedAt` across all entries, or null when the map is empty. */
function getMostRecentCompletedAt(actions: RecentActionsMap): number | null {
    const timestamps = Object.values(actions).map((entry) => entry.completedAt);
    if (timestamps.length === 0) return null;
    return Math.max(...timestamps);
}

function readStoredActions(): RecentActionsMap {
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed: unknown = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return {};
        const result: RecentActionsMap = {};
        for (const [patientId, entry] of Object.entries(parsed as Record<string, unknown>)) {
            if (isRecentActionEntry(entry)) {
                result[patientId] = entry;
            }
        }
        // The window is shared across every held entry, so it's all-or-nothing:
        // if the most recent action is already outside the window, the whole
        // map is stale and gets discarded rather than partially kept.
        const mostRecent = getMostRecentCompletedAt(result);
        if (mostRecent === null) return {};
        if (Date.now() - mostRecent >= RECENT_ACTION_WINDOW_MS) return {};
        return result;
    } catch {
        return {};
    }
}

function writeStoredActions(actions: RecentActionsMap): void {
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(actions));
    } catch {
        // sessionStorage may be unavailable (private browsing, quota, disabled
        // storage) - the in-memory state still drives the UI for this session.
    }
}

function capToMostRecent(actions: RecentActionsMap, max: number): RecentActionsMap {
    const entries = Object.entries(actions);
    if (entries.length <= max) return actions;
    const kept = entries.sort(([, a], [, b]) => b.completedAt - a.completedAt).slice(0, max);
    return Object.fromEntries(kept);
}

interface RecentActionsProviderProps {
    children: ReactNode;
}

/**
 * Tracks actions the current user has just resolved on the patients table so
 * their rows can keep their action-required sort slot (and show a "completed"
 * indicator) instead of reshuffling the moment the server-side
 * `requiredAction` flips to 'None' on refetch.
 *
 * Uses a single GLOBAL SLIDING WINDOW rather than a per-entry TTL: the whole
 * map stays alive until RECENT_ACTION_WINDOW_MS after the most recent
 * `markActionCompleted` call, so while staff keep resolving actions nothing
 * re-sorts. Once the window elapses with no new activity, every held entry
 * is cleared together and all those rows settle back into their normal
 * sorted position at once.
 *
 * Mounted above both the patients list page and the per-patient action pages
 * (which navigate back to '/home' on success) so every action-completion
 * site can reach it.
 */
export function RecentActionsProvider({ children }: RecentActionsProviderProps) {
    const [recentActions, setRecentActions] = useState<RecentActionsMap>(() => readStoredActions());

    // Persisting here (rather than inside the setState updater) keeps the
    // updater pure - React 18 Strict Mode invokes updaters twice, which would
    // otherwise double-write sessionStorage on every call.
    useEffect(() => {
        writeStoredActions(recentActions);
    }, [recentActions]);

    // Schedules a single timer for the shared window's deadline - window
    // length after the most recent entry's completedAt - and clears the
    // entire map when it fires. Any markActionCompleted call changes
    // `recentActions` (bumping the most recent completedAt), which re-runs
    // this effect and reschedules the deadline, sliding the window forward.
    useEffect(() => {
        const mostRecent = getMostRecentCompletedAt(recentActions);
        if (mostRecent === null) return;

        const deadline = mostRecent + RECENT_ACTION_WINDOW_MS;
        const delay = Math.max(0, deadline - Date.now());

        const timeoutId = window.setTimeout(() => {
            setRecentActions({});
        }, delay);

        return () => window.clearTimeout(timeoutId);
    }, [recentActions]);

    const markActionCompleted = useCallback((patientId: string, actionLabel: string) => {
        if (!patientId || !actionLabel) return;
        const completedAt = Date.now();
        setRecentActions((prev) =>
            capToMostRecent(
                { ...prev, [patientId]: { actionLabel, completedAt } },
                MAX_RECENT_ACTIONS
            )
        );
    }, []);

    const value = useMemo(
        () => ({ recentActions, markActionCompleted }),
        [recentActions, markActionCompleted]
    );

    return <RecentActionsContext.Provider value={value}>{children}</RecentActionsContext.Provider>;
}

export function useRecentActions(): RecentActionsContextType {
    const context = useContext(RecentActionsContext);
    if (!context) {
        throw new Error('useRecentActions must be used within RecentActionsProvider');
    }
    return context;
}
